import React, { useEffect, useState } from 'react';
import DataTable from '../../../ui/DataTable/DataTable';
import { CellNumber, CellMoney } from '../../../ui/DataTable/cells';
import Button from '../../../ui/Button/Button';
import { Input, Select } from '../../../ui/Field/Field';
import { ICONS } from '../../../ui/icon-map';
import { finesService, errorMessage } from '../../../services/finesService';
import { Field, Pill } from './ui';
import FinesTab from './FinesTab';

const BY = [
    { id: 'fine', label: 'Violation date', hint: 'the day the fine happened' },
    { id: 'seen', label: 'Detected date', hint: 'the day the engine first recorded it' },
];

const fmtDay = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
};

/**
 * Fines split by day, back in time: pick a day (calendar or the list) and the
 * fines of that day open underneath with the normal filters.
 */
export default function DaysTab() {
    const [by, setBy] = useState('fine');
    const [days, setDays] = useState(null);
    const [error, setError] = useState(null);
    const [day, setDay] = useState('');

    useEffect(() => {
        setError(null); setDays(null);
        finesService.days(by).then(setDays).catch((e) => setError(errorMessage(e)));
    }, [by]);

    const filters = day ? (by === 'fine' ? { date_from: day, date_to: day } : { first_seen: day }) : null;
    const total = days ? days.reduce((a, d) => a + d.count, 0) : 0;

    const columns = [
        { key: 'day', header: 'Day', cell: (d) => <span className="ds-cell-date">{fmtDay(d.day)}</span> },
        { key: 'count', header: 'Fines', type: 'number' },
        { key: 'unpaid', header: 'Unpaid', cell: (d) => <CellNumber value={d.unpaid || ''} tone={d.unpaid ? 'warning' : undefined} /> },
        { key: 'paid', header: 'Paid', cell: (d) => <CellNumber value={d.paid || ''} tone={d.paid ? 'success' : undefined} /> },
        { key: 'amount', header: 'Amount', accessor: (d) => d.billed ?? d.amount, cell: (d) => <CellMoney value={Number(d.billed ?? d.amount ?? 0)} currency="AED" /> },
    ];

    return (
        <div>
            <div className="flex flex-wrap gap-3 items-end mb-3">
                <Field label="Split by">
                    <Select value={by} onChange={(e) => { setBy(e.target.value); setDay(''); }}
                            options={BY.map((b) => ({ value: b.id, label: b.label }))} />
                </Field>
                <Field label="Day">
                    <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
                </Field>
                {day && <Button onClick={() => setDay('')}>All days</Button>}
                <span className="text-xs text-gray-500 self-center">{BY.find((b) => b.id === by)?.hint}{days ? ` · ${days.length} days · ${total} fines` : ''}</span>
            </div>
            {error && filters && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {filters ? (
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Pill tone="indigo">{fmtDay(day)}</Pill>
                        <span className="text-sm text-gray-600">{by === 'fine' ? 'fines that happened on this day' : 'fines first detected on this day'}</span>
                    </div>
                    <FinesTab initialFilters={filters} />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={days || []}
                    rowKey="day"
                    loading={!days && !error}
                    error={error}
                    searchable={false}
                    columnToggle={false}
                    pageSize={50}
                    onRowClick={(d) => setDay(d.day)}
                    emptyIcon={ICONS.calendar}
                    emptyTitle="No fines yet"
                />
            )}
        </div>
    );
}
